import React, { useEffect, useState } from "react";

import "../../components/AddProduct.css";
import { useParams } from "react-router-dom";
import { GetChartAccountData } from "../../store/chartOfAccountAtom";

const buildHierarchy = (items) => {
  const map = {};
  const roots = [];

  items.forEach((item) => {
    map[item.Account_Id] = { ...item, key: item.Account_Id, children: [] };
  });

  items.forEach((item) => {
    if (item.Parent_Account_Id && map[item.Parent_Account_Id]) {
      map[item.Parent_Account_Id].children.push(map[item.Account_Id]);
    } else {
      roots.push(map[item.Account_Id]);
    }
  });

  return roots;
};

// Connector segment for one indentation column.
//   "line"  -> vertical rail passing through
//   "blank" -> empty column
//   "mid"   -> elbow for a middle child (├)
//   "end"   -> elbow for the last child (└)
const TreeConnector = ({ type }) => (
  <span className="relative w-4 shrink-0 self-stretch">
    {(type === "line" || type === "mid") && (
      <span className="absolute bottom-0 top-0 left-1/2 w-0.5 -translate-x-1/2 bg-gray-500" />
    )}
    {type === "end" && (
      <span className="absolute top-0 left-1/2 h-1/2 w-0.5 -translate-x-1/2 bg-gray-500" />
    )}
    {(type === "mid" || type === "end") && (
      <span className="absolute left-1/2 right-0 top-1/2 h-0.5 -translate-y-1/2 bg-gray-500" />
    )}
  </span>
);

function ChartAccountScroll(props) {
  const getChartAccountData = GetChartAccountData();
  const { chart_id } = useParams();

  const [roots, setRoots] = useState([]);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  useEffect(() => {
    const items = getChartAccountData?.chart_of_accounts;
    if (items?.length) {
      setRoots(buildHierarchy(items));
      // expand every account that is a parent of another account
      const parentIds = items
        .filter((p) => items.some((c) => c.Parent_Account_Id === p.Account_Id))
        .map((p) => p.Account_Id);
      setExpandedIds(new Set(parentIds));
    }
  }, [getChartAccountData?.chart_of_accounts]);

  const toggle = (id) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const renderLevel = (nodes, parentRails) =>
    nodes.map((node, idx) => {
      const isLast = idx === nodes.length - 1;
      const depth = parentRails.length;
      const hasChildren = node.children?.length > 0;
      const isExpanded = expandedIds.has(node.Account_Id);
      const isSelected = String(node.Account_Id) === String(chart_id);

      const connectors = [];
      for (let i = 0; i < depth; i++) {
        if (i < depth - 1) {
          connectors.push(parentRails[i] ? "line" : "blank");
        } else {
          connectors.push(isLast ? "end" : "mid");
        }
      }

      return (
        <div key={node.Account_Id}>
          <div className="flex min-h-[32px] items-stretch">
            {connectors.map((type, i) => (
              <TreeConnector key={i} type={type} />
            ))}

            {hasChildren ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(node.Account_Id);
                }}
                className="mr-1 flex h-5 w-5 shrink-0 items-center justify-center self-center rounded text-gray-500 hover:bg-gray-200"
                aria-label={isExpanded ? "Collapse" : "Expand"}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  className={`h-3 w-3 transition-transform ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            ) : (
              <span className="mr-1 inline-block w-5 shrink-0" />
            )}

            <button
              onClick={() => props.viewItemDetails(node.Account_Id)}
              className={`flex-1 self-center truncate rounded px-2 py-1 text-left text-sm transition-colors ${
                isSelected
                  ? "bg-blue-50 font-semibold text-blue-700"
                  : "text-gray-700 hover:bg-gray-100"
              }`}
              title={node.Account_Name}
            >
              {node.Account_Name}
            </button>
          </div>

          {hasChildren &&
            isExpanded &&
            renderLevel(node.children, [...parentRails, !isLast])}
        </div>
      );
    });

  return (
    <section className="w-full max-w-xs h-screen bg-gray-50">
      <section className="w-full h-[calc(100vh-4rem)] mt-3 border border-gray-200 rounded-xl shadow-lg overflow-y-auto">
        <div className="p-4">
          {roots.length ? (
            renderLevel(roots, [])
          ) : (
            <p className="p-4 text-center text-sm text-gray-400">
              No accounts to display
            </p>
          )}
        </div>
      </section>
    </section>
  );
}

export default ChartAccountScroll;
